import React, { useContext, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Button } from '../../ui/button';
import { Edit, Trash2, Plus, CheckSquare, Square, Shield, X } from 'lucide-react';
import LessonCard from './LessonCard';
import LessonAIBuilder from './LessonAIBuilder';
import { stripAndTruncate } from '../../../lib/richText';

const ModuleCard = ({
  module,
  moduleIndex,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onAddLesson,
  onEditLesson,
  onDeleteLesson,
  onBatchGenerated,
}) => {
  const { API } = useContext(AuthContext);
  // Bulk-select mode + which lesson ids are ticked. Kept local to the
  // module so multiple expanded modules can bulk-select independently.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const lessons = module.lessons || [];
  const generatedLessons = useMemo(
    () => lessons.filter((l) => l.content_generated),
    [lessons],
  );
  const selectedList = useMemo(
    () => lessons.filter((l) => selectedIds.has(l.id)),
    [lessons, selectedIds],
  );

  const toggleSelect = (lessonId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });
  };

  const selectAllGenerated = () => {
    setSelectedIds(new Set(generatedLessons.map((l) => l.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };

  const bulkMark = async (aiFinal) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/lms/lessons/bulk-ai-final`, {
        lesson_ids:       Array.from(selectedIds),
        ai_content_final: aiFinal,
      });
      toast.success(
        aiFinal
          ? `Marked ${data?.updated || selectedIds.size} lesson${(data?.updated || selectedIds.size) === 1 ? '' : 's'} as final`
          : `Cleared final on ${data?.updated || selectedIds.size} lesson${(data?.updated || selectedIds.size) === 1 ? '' : 's'}`,
      );
      // Ping the parent so it can refetch course details and refresh
      // the per-lesson badges. onBatchGenerated is the same callback
      // ModuleCard already uses after AI runs, so we reuse it.
      onBatchGenerated?.({ bulk_final: true, updated: data?.updated || 0 });
      exitSelectMode();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border-2 border-slate-200 dark:border-slate-700">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-slate-900 dark:text-white">
              Module {moduleIndex + 1}: {module.title}
            </h4>
            <span className="text-xs text-slate-500 dark:text-neutral-500">
              ({lessons.length} lessons)
            </span>
          </div>
          {module.description && !isExpanded && (
            <p className="text-sm text-slate-600 dark:text-neutral-400 mt-1 line-clamp-1">
              {stripAndTruncate(module.description, 140)}
            </p>
          )}
          {module.ad_enabled && (
            <span className="inline-block mt-2 px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
              📢 Ad Enabled
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onToggle}>
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Expanded Module Content */}
      {isExpanded && (
        <>
          {module.description && (
            <div
              className="text-sm text-slate-600 dark:text-neutral-400 mb-3 prose dark:prose-invert max-w-none prose-sm"
              dangerouslySetInnerHTML={{ __html: module.description }}
            />
          )}

          {/* Action row: Select mode toggle + Add lesson. Select mode
              flips every LessonCard into checkbox mode so admins can
              bulk-mark a reviewed batch as final in one shot instead of
              clicking each row's individual toggle. */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <Button
              size="sm"
              variant={selectMode ? 'default' : 'outline'}
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              data-testid={`module-select-mode-${module.id}`}
              className={selectMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
            >
              {selectMode ? (
                <><X className="w-4 h-4 mr-1.5" /> Exit select mode</>
              ) : (
                <><CheckSquare className="w-4 h-4 mr-1.5" /> Select lessons</>
              )}
            </Button>
            <Button size="sm" onClick={onAddLesson} className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Add Lesson to this Module
            </Button>
          </div>

          {/* Bulk action bar — visible when at least one lesson ticked.
              Sits above the batch AI generator so it takes precedence
              when the admin is actively selecting. */}
          {selectMode && (
            <div
              className="mb-3 rounded-md border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-3 flex flex-wrap items-center gap-2"
              data-testid={`module-bulk-bar-${module.id}`}
            >
              <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                {selectedIds.size} of {lessons.length} selected
              </span>
              <button
                type="button"
                onClick={selectAllGenerated}
                className="text-xs px-2 py-0.5 rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-900 hover:border-blue-500 text-blue-800 dark:text-blue-200"
                data-testid={`module-bulk-select-generated-${module.id}`}
                title="Select every lesson in this module that already has AI-generated content"
              >
                Select all generated ({generatedLessons.length})
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs px-2 py-0.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 hover:border-slate-500 text-slate-700 dark:text-neutral-300"
                data-testid={`module-bulk-clear-${module.id}`}
              >
                Clear
              </button>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulkMark(false)}
                disabled={selectedIds.size === 0 || bulkBusy}
                data-testid={`module-bulk-unmark-${module.id}`}
                className="border-slate-300 dark:border-slate-600"
                title="Remove the Final flag from every selected lesson"
              >
                Unmark final
              </Button>
              <Button
                size="sm"
                onClick={() => bulkMark(true)}
                disabled={selectedIds.size === 0 || bulkBusy}
                data-testid={`module-bulk-mark-final-${module.id}`}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Shield className="w-4 h-4 mr-1.5" />
                {bulkBusy ? 'Working…' : `Mark ${selectedIds.size || ''} as final`}
              </Button>
            </div>
          )}

          {/* Batch AI content generator — same element choices applied
              to every lesson in this module, one at a time. Skipped when
              the module has no lessons yet. Hidden while in select mode
              so the bulk-action UI stays the primary flow. */}
          {!selectMode && lessons.length > 0 && (
            <div className="mb-3">
              <LessonAIBuilder
                moduleId={module.id}
                lessonCount={lessons.length}
                onGenerated={onBatchGenerated}
              />
            </div>
          )}

          {/* Lessons */}
          {lessons.length > 0 ? (
            <div className="ml-4 space-y-2 mt-3">
              {lessons.map((lesson, lessonIndex) => (
                <LessonCard
                  key={lesson.id}
                  lesson={lesson}
                  lessonIndex={lessonIndex}
                  selectable={selectMode}
                  selected={selectedIds.has(lesson.id)}
                  onToggleSelect={() => toggleSelect(lesson.id)}
                  onEdit={() => onEditLesson(lesson)}
                  onDelete={() => onDeleteLesson(lesson.id)}
                  onGenerated={onBatchGenerated}
                />
              ))}
            </div>
          ) : (
            <p className="text-slate-500 dark:text-neutral-500 text-sm ml-4 mt-3">
              No lessons in this module yet.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default ModuleCard;
